import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Columns2, X } from 'lucide-react';

// Shared labels — used by the per-module trigger and the mobile pane toggle
// in the SplitViewLayout itself. Keep this list in sync with renderTab() in
// ProjectWorkspace.js.
export const SPLIT_TAB_LABELS = {
  plot:       'Plot Forge',
  characters: 'Character Lab',
  scenes:     'Scene Builder',
  screenplay: 'Screenplay',
  bible:      'Story Bible',
  notes:      'Notes & References',
  web:        'Character Web',
  world:      'World Building',
};

/**
 * SplitViewTrigger — the "Open split view" button + module-picker dropdown
 * that lives inside each module's own header row.
 *
 * Props:
 *   primaryTab    string — the tab id of the module rendering this trigger
 *   splitView     null | { left, right } — current state from ProjectWorkspace
 *   onChangeSplit (next) => void — pass null to exit
 *   availableTabs optional string[] — restrict the pairing picker to this
 *                 subset (e.g. Series Builder only has notes/characters/
 *                 web/world). Omit to allow every module.
 */
export function SplitViewTrigger({ primaryTab, splitView, onChangeSplit, availableTabs, labels = SPLIT_TAB_LABELS, pane }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const isActive = Boolean(splitView?.right);
  const partner = splitView?.right;

  // Click-outside closes the dropdown
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Don't list the current primary tab as a pairing option; when a host
  // passes availableTabs, only offer modules it actually renders.
  const options = Object.entries(labels).filter(
    ([id]) => id !== primaryTab && (!availableTabs || availableTabs.includes(id))
  );

  return (
    <div ref={wrapperRef} className="relative flex-shrink-0">
      {isActive ? (
        // Only the primary (left) pane owns the exit control — rendering it
        // in both panes gave two "Exit split" buttons per screen. Hosts pass
        // pane: 'left' | 'right' explicitly; anything that doesn't (single-pane
        // renders, legacy callers) is treated as primary so the exit control
        // is never unreachable. The mobile toggle keeps its own ✕ either way.
        pane !== 'right' ? (
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-widest text-primary whitespace-nowrap">
              ↔ {labels[partner] || partner}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onChangeSplit(null)}
              data-testid="split-exit-btn"
              className="rounded-none font-mono uppercase text-[10px] tracking-widest"
            >
              <X className="w-3 h-3 mr-1" /> Exit split
            </Button>
          </div>
        ) : null
      ) : (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOpen((o) => !o)}
          data-testid="split-open-btn"
          className="rounded-none font-mono uppercase text-[10px] tracking-widest"
        >
          <Columns2 className="w-3 h-3 mr-1" /> Split view
        </Button>
      )}

      {open && !isActive && (
        <div
          className="absolute right-0 top-full mt-2 z-30 bg-card border border-border/40 shadow-lg min-w-[220px]"
          data-testid="split-picker"
        >
          <div className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border/40">
            Pair with…
          </div>
          {options.map(([id, label]) => (
            <button
              key={id}
              onClick={() => {
                onChangeSplit({ left: primaryTab, right: id });
                setOpen(false);
              }}
              data-testid={`split-pick-${id}`}
              className="w-full text-left px-3 py-2 font-mono text-xs hover:bg-primary/10 hover:text-primary transition-colors"
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * SplitViewLayout — two side-by-side panes with a draggable divider on
 * desktop, a stacked toggle on mobile (below md breakpoint).
 *
 * Props:
 *   left          ReactNode rendered in the primary pane
 *   right         ReactNode rendered in the secondary pane
 *   leftLabel     string shown in the mobile toggle bar (e.g. "Plot Forge")
 *   rightLabel    string shown in the mobile toggle bar
 *   onClose       optional () => void — called when user clicks the X to exit
 *
 * No external deps — implements the drag with raw mouse/touch events and
 * stores the divider position as a percentage in component state.
 */
export default function SplitViewLayout({
  left,
  right,
  leftLabel = 'Left',
  rightLabel = 'Right',
  onClose,
}) {
  // Divider position as a percentage of container width. Clamped 25–75 so
  // neither pane ever goes below ~quarter width.
  const [splitPct, setSplitPct] = useState(50);
  const [activeMobile, setActiveMobile] = useState('left'); // 'left' | 'right'
  const dragging = useRef(false);
  const containerRef = useRef(null);

  const onPointerDown = useCallback((e) => {
    dragging.current = true;
    e.preventDefault();
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const pct = Math.max(25, Math.min(75, (x / rect.width) * 100));
      setSplitPct(pct);
    };
    const onUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  return (
    <>
      {/* ── Mobile toggle bar (below md). Not sticky: the workspace header
          is sticky top-0 z-40 and would cover a stuck bar. Labels truncate
          so two module names + the ✕ always fit a 375px viewport. ── */}
      <div className="md:hidden bg-card border-b border-border/40 flex items-stretch">
        <button
          onClick={() => setActiveMobile('left')}
          className={`flex-1 min-w-0 px-2 py-3 font-mono text-[11px] uppercase tracking-wider truncate transition-colors ${
            activeMobile === 'left'
              ? 'bg-primary/10 text-primary border-b-2 border-primary'
              : 'text-muted-foreground border-b-2 border-transparent'
          }`}
          data-testid="split-mobile-left"
        >
          {leftLabel}
        </button>
        <button
          onClick={() => setActiveMobile('right')}
          className={`flex-1 min-w-0 px-2 py-3 font-mono text-[11px] uppercase tracking-wider truncate transition-colors ${
            activeMobile === 'right'
              ? 'bg-primary/10 text-primary border-b-2 border-primary'
              : 'text-muted-foreground border-b-2 border-transparent'
          }`}
          data-testid="split-mobile-right"
        >
          {rightLabel}
        </button>
        {onClose && (
          <button
            onClick={onClose}
            className="flex-shrink-0 px-3 py-3 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground border-b-2 border-transparent"
            data-testid="split-mobile-close"
            title="Exit split view"
          >
            ✕
          </button>
        )}
      </div>

      {/* ── Mobile single-pane render ── */}
      <div className="md:hidden">
        <div className={activeMobile === 'left' ? 'block' : 'hidden'}>{left}</div>
        <div className={activeMobile === 'right' ? 'block' : 'hidden'}>{right}</div>
      </div>

      {/* ── Desktop split (md and up) ── */}
      <div
        ref={containerRef}
        className="hidden md:flex relative w-full"
        style={{ minHeight: '60vh' }}
        data-testid="split-desktop"
      >
        <div
          className="overflow-y-auto"
          style={{ width: `${splitPct}%`, paddingRight: 16 }}
          data-testid="split-pane-left"
        >
          {left}
        </div>

        {/* Draggable divider */}
        <div
          onMouseDown={onPointerDown}
          onTouchStart={onPointerDown}
          className="relative w-1 bg-border/40 hover:bg-primary/60 cursor-col-resize transition-colors flex-shrink-0"
          style={{ touchAction: 'none' }}
          data-testid="split-divider"
        >
          {/* Visual grip — slightly wider hit area, visible on hover */}
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-12 w-1 bg-primary/0 hover:bg-primary/80 rounded-none transition-colors"
            style={{ pointerEvents: 'none' }}
          />
          <div
            className="absolute inset-y-0 -inset-x-2"
            style={{ pointerEvents: 'none' }}
          />
        </div>

        <div
          className="overflow-y-auto"
          style={{ width: `${100 - splitPct}%`, paddingLeft: 16 }}
          data-testid="split-pane-right"
        >
          {right}
        </div>
      </div>
    </>
  );
}
